import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Award, FileDown } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { api } from '@/client/lib/api';

type CertRow = {
  certId: string;
  courseTitle: string;
  courseSlug: string | null;
  userFullName: string;
  issuedAt: number;
  revokedAt: number | null;
  downloadUrl: string | null;
  verificationUrl: string;
  ready: boolean;
};

// Surfaces the cert download CTA on the portal course detail. Fetches the
// list of the user's certs and picks the matching one by courseSlug. While
// the PDF is still generating (waitUntil in flight), we show a "pripravujem"
// label and disable the button. Polls every 5s until ready.
export function CertificateDownloadButton({ courseSlug }: { courseSlug: string }) {
  const { t } = useTranslation();
  const query = useQuery<CertRow[]>({
    queryKey: ['portal', 'certificates'],
    queryFn: () => api<CertRow[]>('/api/v1/me/certificates'),
    refetchInterval: (q) => {
      // Poll while any cert for this course isn't ready yet.
      const list = q.state.data;
      if (!list) return false;
      const mine = list.find((c) => c.courseSlug === courseSlug);
      return mine && !mine.ready ? 5000 : false;
    },
  });

  const cert = query.data?.find((c) => c.courseSlug === courseSlug && !c.revokedAt);

  if (!cert) {
    return (
      <Button variant="secondary" size="sm" disabled>
        <Award size={16} />
        {t('certificates.preparing')}
      </Button>
    );
  }

  if (!cert.ready || !cert.downloadUrl) {
    return (
      <Button variant="secondary" size="sm" disabled>
        <Award size={16} className="animate-pulse" />
        {t('certificates.preparing')}
      </Button>
    );
  }

  return (
    <a href={cert.downloadUrl} className="inline-flex">
      <Button variant="primary" size="sm">
        <FileDown size={16} />
        {t('certificates.download')}
      </Button>
    </a>
  );
}
